/**
 * Stage-driven KPI strip for the assessment detail header.
 *
 * ≤4 tiles per stage (`KeyMetrics` max-four). Trend chips only when both
 * direction and a numeric `delta` exist — never an arrow alone.
 *
 * **Draft:** paper still editable — questions, points, time, need-review.
 * **Scheduled+:** paper frozen — no readiness / need-review tiles.
 */

import type { MetricItem } from "@exxatdesignux/ui/components/key-metrics"
import { formatDateUS } from "@/lib/date-filter"
import { SCORING_KPI_LABEL } from "@/lib/grading/copy"

/**
 * Do not pass KeyMetrics `alert: "warning"`. Package warning ink is `--chart-4`
 * on an 8% wash (measured 2.97:1). AA text is 4.5:1. `danger` uses
 * `text-destructive-ink` (~4.6:1 on the wash).
 */
const ATTENTION = "danger" as const

export type AssessmentLifecycleStatus =
  | "Draft"
  | "Scheduled"
  | "Live"
  | "Evaluating"
  | "Results Published"

export interface AssessmentDetailKpiContext {
  status: AssessmentLifecycleStatus | string
  /** Ready (non-draft) questions on the paper. */
  questionCount: number
  targetQuestionCount: number
  totalPoints: number
  targetPoints: number
  needsReviewCount: number
  /** Estimated sitting time from item history; null when unknown. */
  estimatedMinutes: number | null
  targetMinutes: number
  /**
   * When false, quota tiles show actuals only and nudge authors to set blueprint
   * targets instead of comparing to derived mock totals.
   */
  targetsConfigured?: boolean
  /** When false, need review and PBI tiles explain stats come after sitting. */
  itemAnalysisAvailable?: boolean
  /** When false, need review count stays unset until author picks a floor. */
  reviewThresholdConfigured?: boolean
  /** When false, item analysis and review flags wait on blueprint setup. */
  hasBlueprintTargets?: boolean
  avgPbi: string | null
  avgDiscIndex: string | null
  reliabilityIndex: string | null
  enrolledCount: number
  startedCount: number
  submittedCount: number
  gradedCount: number
  pendingManualCount: number
  /** Hand-scored question slots across active submissions (from grading state). */
  manualSlotsTotal?: number
  manualSlotsScored?: number
  manualCompletePct?: number
  avgScore: number | null
  minScore: number | null
  maxScore: number | null
  /** Max points on a fully scored submission (for low-score alerts). */
  scorePossible?: number | null
  /** Graded-score counts by band. Drives the Avg score mini chart (not a progress fill). */
  scoreBands: { label: string; value: number }[]
  passRate: number | null
  autoGradedPercent: number | null
  /** Mock countdown until live window opens (Scheduled only). */
  timeToLiveLabel?: string
}

function formatMinutes(minutes: number | null): string {
  if (minutes == null) return "None yet"
  return Number.isInteger(minutes) ? String(minutes) : minutes.toFixed(1)
}

export type AssessmentHeaderMetaContext = {
  status: AssessmentLifecycleStatus | string
  courseCode: string
  courseTitle?: string | null
  assessmentType: string
  startDate: string
  endDate?: string
  resultsPublishedDate?: string | null
  timeToLiveLabel?: string
}

/** PageHeader subtitle segments (join with · in the view). */
export function assessmentHeaderMetaSegments(
  ctx: AssessmentHeaderMetaContext,
): string[] {
  const courseLabel = ctx.courseTitle
    ? `${ctx.courseCode} · ${ctx.courseTitle}`
    : ctx.courseCode

  const segments = [courseLabel, `Type: ${ctx.assessmentType}`]

  switch (ctx.status) {
    case "Draft":
      break
    case "Scheduled":
      segments.push(`Live Date: ${formatDateUS(ctx.startDate)}`)
      if (ctx.timeToLiveLabel) {
        segments.push(`Opens in ${ctx.timeToLiveLabel}`)
      }
      break
    case "Live":
      if (ctx.endDate && ctx.endDate !== ctx.startDate) {
        segments.push(
          `Live: ${formatDateUS(ctx.startDate)} to ${formatDateUS(ctx.endDate)}`,
        )
      } else {
        segments.push(`Live Date: ${formatDateUS(ctx.startDate)}`)
      }
      break
    case "Evaluating":
      segments.push(`Live Date: ${formatDateUS(ctx.startDate)}`)
      break
    case "Results Published":
      segments.push(`Live Date: ${formatDateUS(ctx.startDate)}`)
      if (ctx.resultsPublishedDate) {
        segments.push(
          `Results published: ${formatDateUS(ctx.resultsPublishedDate)}`,
        )
      }
      break
    default:
      segments.push(`Live Date: ${formatDateUS(ctx.startDate)}`)
  }

  return segments
}


function quotaMetricValue(
  actual: number,
  target: number,
  formatPart: (value: number) => string = String,
  targetsConfigured = true,
): Pick<MetricItem, "value"> {
  if (!targetsConfigured || !(target > 0)) {
    return { value: formatPart(actual) }
  }
  return {
    value: `${formatPart(actual)}/${formatPart(target)}`,
  }
}

function estimatedTimeMetricValue(
  estimatedMinutes: number | null,
  targetMinutes: number,
  targetsConfigured = true,
): Pick<MetricItem, "value"> {
  if (!targetsConfigured || !(targetMinutes > 0)) {
    return { value: formatMinutes(estimatedMinutes) }
  }
  return {
    value: `${formatMinutes(estimatedMinutes)} / ${formatMinutes(targetMinutes)}`,
  }
}

import type { AssessmentBlueprintPhase } from "@/lib/assessment-blueprint-phase"

/** Blueprint setup nudge for the Questions tab (none or partial blueprint only). */
export function assessmentBlueprintBannerCopy(input: {
  marksOnPaper: number
  phase: Extract<AssessmentBlueprintPhase, "none" | "partial">
}): { title: string; body: string } {
  if (input.phase === "none") {
    return {
      title: "Add a blueprint",
      body: `${input.marksOnPaper} marks on the paper. Define topic coverage in the blueprint before setting exam targets.`,
    }
  }
  return {
    title: "Finish the blueprint",
    body: "Topic coverage is started. Set targets for every topic, difficulty band, and Bloom level before locking the blueprint.",
  }
}

/** @deprecated Use assessmentBlueprintBannerCopy for blueprint phases. */
export function assessmentSetupBannerCopy(input: {
  marksOnPaper: number
  phase: AssessmentBlueprintPhase
}): { title: string; body: string } {
  if (input.phase === "none" || input.phase === "partial") {
    return assessmentBlueprintBannerCopy({
      marksOnPaper: input.marksOnPaper,
      phase: input.phase,
    })
  }
  return {
    title: "Blueprint complete",
    body: "Blueprint and targets are set. Build sections and add questions to match coverage.",
  }
}

export const QUOTA_KPI_IDS_WHEN_UNCONFIGURED = [
  "questions",
  "points",
  "estimated-time",
  "questions-locked",
] as const


const ITEM_STATS_PENDING_NUDGE =
  "Review flags unlock after the first sitting"

const REVIEW_THRESHOLD_NUDGE =
  "Choose a discrimination floor in the blueprint"

const REVIEW_BLUEPRINT_NUDGE =
  "Set topic coverage and choose a review floor in the blueprint."

function needsReviewMetricFields(
  ctx: Pick<
    AssessmentDetailKpiContext,
    | "needsReviewCount"
    | "itemAnalysisAvailable"
    | "reviewThresholdConfigured"
    | "hasBlueprintTargets"
  >,
): Pick<MetricItem, "value" | "description" | "alert" | "trendPolarity"> {
  if (ctx.itemAnalysisAvailable === false) {
    if (ctx.needsReviewCount > 0) {
      return {
        value: ctx.needsReviewCount,
        description: "Authoring Review checks on this paper",
        trendPolarity: "lower_is_better",
        alert: ctx.needsReviewCount >= 5 ? "danger" : ATTENTION,
      }
    }
    return {
      value: "None yet",
      description: ITEM_STATS_PENDING_NUDGE,
      trendPolarity: "lower_is_better",
      alert: undefined,
    }
  }
  if (ctx.needsReviewCount > 0) {
    return {
      value: ctx.needsReviewCount,
      trendPolarity: "lower_is_better",
      alert: ctx.needsReviewCount >= 5 ? "danger" : ATTENTION,
    }
  }
  if (ctx.hasBlueprintTargets === false) {
    return {
      value: "Not set",
      description: REVIEW_BLUEPRINT_NUDGE,
      trendPolarity: "lower_is_better",
      alert: undefined,
    }
  }
  if (ctx.reviewThresholdConfigured === false) {
    return {
      value: "Not set",
      description: REVIEW_THRESHOLD_NUDGE,
      trendPolarity: "lower_is_better",
      alert: undefined,
    }
  }
  return {
    value: ctx.needsReviewCount,
    description: undefined,
    trendPolarity: "lower_is_better",
    alert: undefined,
  }
}

/**
 * Quota meter for Questions / Points / Est. time. Same bar on every tile.
 * Fill is always actual / target, matching the `actual / target` value.
 * KeyMetrics clamps at 100%, so overshoot is a full danger bar, not an
 * inverted short fill that reads as still under quota.
 */
function quotaMeter(
  actual: number,
  target: number,
  opts: { overAlert: MetricItem["alert"]; onTone: "success" | "brand" },
): Pick<MetricItem, "progress" | "progressMax" | "progressTone" | "alert"> {
  if (!(target > 0)) {
    return {}
  }
  const over = actual > target
  return {
    progress: actual,
    progressMax: target,
    progressTone: over ? "danger" : actual === target ? opts.onTone : "brand",
    alert: over ? opts.overAlert : undefined,
  }
}

const SCORE_BANDS = [
  { label: "Below 70", min: 0, max: 70 },
  { label: "70 to 79", min: 70, max: 80 },
  { label: "80 to 89", min: 80, max: 90 },
  { label: "90 to 100", min: 90, max: 101 },
] as const

/** Histogram of graded scores. An average is a distribution, not a quota fill. */
export function scoreBandSeries(scores: number[]): { label: string; value: number }[] {
  return SCORE_BANDS.map((band) => ({
    label: band.label,
    value: scores.filter((score) => score >= band.min && score < band.max).length,
  }))
}

function avgScoreChart(
  ctx: Pick<AssessmentDetailKpiContext, "avgScore" | "minScore" | "maxScore">,
): MetricItem["chart"] {
  if (ctx.avgScore == null || ctx.minScore == null || ctx.maxScore == null) {
    return undefined
  }
  return {
    kind: "sparkline",
    tone: ctx.avgScore < 70 ? ATTENTION : "success",
    series: [
      { value: ctx.minScore, label: "Min" },
      { value: ctx.avgScore, label: "Avg" },
      { value: ctx.maxScore, label: "Max" },
    ],
  }
}

function formatScoreValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

export function formatScoreRangeLabel(min: number, max: number): string {
  return `Min. ${formatScoreValue(min)} · Max ${formatScoreValue(max)}`
}

function avgScoreAlertThreshold(
  ctx: Pick<AssessmentDetailKpiContext, "avgScore" | "scorePossible">,
): number {
  if (ctx.scorePossible != null && ctx.scorePossible > 0) {
    return ctx.scorePossible * 0.7
  }
  return 70
}

function avgScoreTile(ctx: AssessmentDetailKpiContext): MetricItem {
  const hasRange = ctx.minScore != null && ctx.maxScore != null
  return tile({
    id: "avg-score",
    label: "Avg score",
    value:
      ctx.avgScore != null ? formatScoreValue(ctx.avgScore) : "None yet",
    description: hasRange
      ? formatScoreRangeLabel(ctx.minScore!, ctx.maxScore!)
      : undefined,
    chart: avgScoreChart(ctx),
    alert:
      ctx.avgScore != null && ctx.avgScore < avgScoreAlertThreshold(ctx)
        ? ATTENTION
        : undefined,
  })
}

/** Snapshot tile — no trend chip (no fabricated direction without a delta). */
function tile(
  item: Omit<MetricItem, "trend" | "delta"> &
    Partial<Pick<MetricItem, "trend" | "delta" | "trendPolarity" | "alert">>,
): MetricItem {
  return {
    trend: "neutral",
    delta: "",
    ...item,
  }
}

/**
 * Draft — Is the assessment structured and covering the blueprint?
 * Questions · Points · Est. time · Need review
 */
function buildingMetrics(ctx: AssessmentDetailKpiContext): MetricItem[] {
  const targetsConfigured = ctx.targetsConfigured !== false
  return [
    tile({
      id: "questions",
      label: "Questions",
      ...quotaMetricValue(
        ctx.questionCount,
        ctx.targetQuestionCount,
        String,
        targetsConfigured,
      ),
      ...quotaMeter(
        ctx.questionCount,
        targetsConfigured ? ctx.targetQuestionCount : 0,
        {
          overAlert: ATTENTION,
          onTone: "success",
        },
      ),
    }),
    tile({
      id: "points",
      label: "Points",
      ...quotaMetricValue(
        ctx.totalPoints,
        ctx.targetPoints,
        String,
        targetsConfigured,
      ),
      ...quotaMeter(
        ctx.totalPoints,
        targetsConfigured ? ctx.targetPoints : 0,
        {
          overAlert: "danger",
          onTone: "brand",
        },
      ),
    }),
    tile({
      id: "estimated-time",
      label: "Est. time (min)",
      ...estimatedTimeMetricValue(
        ctx.estimatedMinutes,
        ctx.targetMinutes,
        targetsConfigured,
      ),
      ...quotaMeter(
        ctx.estimatedMinutes ?? 0,
        targetsConfigured ? ctx.targetMinutes : 0,
        {
          overAlert: ATTENTION,
          onTone: "brand",
        },
      ),
    }),
    tile({
      id: "needs-review",
      label: "Need review",
      ...needsReviewMetricFields(ctx),
    }),
  ]
}

/**
 * Scheduled — paper locked; release timing only.
 * Questions · Points · Est. time · Time to live
 */
function scheduledMetrics(ctx: AssessmentDetailKpiContext): MetricItem[] {
  const targetsConfigured = ctx.targetsConfigured !== false
  return [
    tile({
      id: "questions-locked",
      label: "Questions",
      ...quotaMetricValue(
        ctx.questionCount,
        ctx.targetQuestionCount,
        String,
        targetsConfigured,
      ),
      ...quotaMeter(
        ctx.questionCount,
        targetsConfigured ? ctx.targetQuestionCount : 0,
        {
          overAlert: ATTENTION,
          onTone: "success",
        },
      ),
    }),
    tile({
      id: "points",
      label: "Points",
      ...quotaMetricValue(
        ctx.totalPoints,
        ctx.targetPoints,
        String,
        targetsConfigured,
      ),
      ...quotaMeter(
        ctx.totalPoints,
        targetsConfigured ? ctx.targetPoints : 0,
        {
          overAlert: "danger",
          onTone: "brand",
        },
      ),
    }),
    tile({
      id: "estimated-time",
      label: "Est. time (min)",
      ...estimatedTimeMetricValue(
        ctx.estimatedMinutes,
        ctx.targetMinutes,
        targetsConfigured,
      ),
      ...quotaMeter(
        ctx.estimatedMinutes ?? 0,
        targetsConfigured ? ctx.targetMinutes : 0,
        {
          overAlert: ATTENTION,
          onTone: "brand",
        },
      ),
    }),
    tile({
      id: "time-to-live",
      label: "Time to live",
      value: ctx.timeToLiveLabel ?? "Soon",
    }),
  ]
}

/**
 * Live — How is the assessment progressing?
 * Started · Submitted · Completion · Auto-graded
 */
function liveMetrics(ctx: AssessmentDetailKpiContext): MetricItem[] {
  const completion =
    ctx.enrolledCount > 0
      ? Math.round((ctx.submittedCount / ctx.enrolledCount) * 100)
      : 0
  const notStarted = Math.max(0, ctx.enrolledCount - ctx.startedCount)
  return [
    tile({
      id: "started",
      label: "Started",
      value: `${ctx.startedCount}/${ctx.enrolledCount}`,
      progress: ctx.startedCount,
      progressMax: ctx.enrolledCount,
      progressTone: notStarted > ctx.enrolledCount * 0.4 ? ATTENTION : "success",
      alert: notStarted > ctx.enrolledCount * 0.4 ? ATTENTION : undefined,
    }),
    tile({
      id: "submitted",
      label: "Submitted",
      value: `${ctx.submittedCount}/${ctx.enrolledCount}`,
      progress: ctx.submittedCount,
      progressMax: ctx.enrolledCount,
      progressTone: "info",
    }),
    tile({
      id: "completion",
      label: "Completion",
      value: `${completion}%`,
      progress: completion,
      progressTone: completion < 50 ? ATTENTION : "success",
      alert: completion < 50 ? ATTENTION : undefined,
    }),
    tile({
      id: "auto-graded",
      label: "Auto-graded",
      value:
        ctx.autoGradedPercent != null ? `${ctx.autoGradedPercent}%` : "None yet",
      progress: ctx.autoGradedPercent ?? 0,
      progressTone: "info",
    }),
  ]
}

/**
 * Evaluating — What scoring work is still pending?
 * Graded · Manual scoring progress · Avg score · Score range
 */
function gradingMetrics(ctx: AssessmentDetailKpiContext): MetricItem[] {
  const scoreRange =
    ctx.minScore != null && ctx.maxScore != null
      ? formatScoreRangeLabel(ctx.minScore, ctx.maxScore)
      : "None yet"
  const gradedPct =
    ctx.submittedCount > 0
      ? Math.round((ctx.gradedCount / ctx.submittedCount) * 100)
      : 0
  const manualSlotsTotal = ctx.manualSlotsTotal ?? 0
  const manualSlotsScored = ctx.manualSlotsScored ?? 0

  return [
    tile({
      id: "graded-pct",
      label: "Graded",
      value: `${ctx.gradedCount}/${ctx.submittedCount}`,
      progress: ctx.gradedCount,
      progressMax: Math.max(ctx.submittedCount, 1),
      progressTone: gradedPct < 50 ? ATTENTION : "success",
    }),
    tile({
      id: "pending-manual",
      label: SCORING_KPI_LABEL.manualScoring,
      value: manualSlotsTotal
        ? `${manualSlotsScored}/${manualSlotsTotal}`
        : "None",
      ...(manualSlotsTotal > 0
        ? {
            progress: manualSlotsScored,
            progressMax: manualSlotsTotal,
            progressTone:
              manualSlotsScored < manualSlotsTotal
                ? ATTENTION
                : ("success" as const),
          }
        : {}),
    }),
    avgScoreTile(ctx),
    tile({
      id: "score-range",
      label: "Score range",
      value: scoreRange,
    }),
  ]
}

/**
 * Results published — How did students perform?
 * Avg score · Pass rate · Avg PBI · Reliability
 */
function completedMetrics(ctx: AssessmentDetailKpiContext): MetricItem[] {
  const pbi = ctx.avgPbi != null ? Number(ctx.avgPbi) : null
  const reliability =
    ctx.reliabilityIndex != null ? Number(ctx.reliabilityIndex) : null
  return [
    avgScoreTile(ctx),
    tile({
      id: "pass-rate",
      label: "Pass rate",
      value: ctx.passRate != null ? `${ctx.passRate}%` : "None yet",
      progress: ctx.passRate ?? 0,
      progressTone:
        ctx.passRate != null && ctx.passRate < 70 ? "danger" : "success",
      alert: ctx.passRate != null && ctx.passRate < 70 ? "danger" : undefined,
    }),
    tile({
      id: "avg-pbi",
      label: "Avg PBI",
      value: ctx.avgPbi ?? "None yet",
      trendPolarity: "higher_is_better",
      alert: pbi != null && pbi < 0.2 ? ATTENTION : undefined,
    }),
    tile({
      id: "reliability",
      label: "Reliability",
      value: ctx.reliabilityIndex ?? "None yet",
      alert: reliability != null && reliability < 0.7 ? ATTENTION : undefined,
    }),
  ]
}

/**
 * A header KPI is a control only when the click applies a filter the number
 * uniquely implies. Quota and score tiles stay read-only.
 */
export type AssessmentKpiClick = "needs-review" | "manual-grading"

export const ASSESSMENT_KPI_CLICK: Record<string, AssessmentKpiClick> = {
  "needs-review": "needs-review",
  "pending-manual": "manual-grading",
}

/**
 * Paper targets and sit-size that vary with the assessment, so two papers
 * do not show the same 18/18 and 110/110 strip.
 */
export function assessmentKpiTargets(assessment: {
  questionCount: number
}): {
  targetQuestions: number
  targetPoints: number
  targetMinutes: number
  enrolledCount: number
  liveStartRate: number
} {
  const questions = Math.max(1, assessment.questionCount)
  return {
    targetQuestions: questions,
    targetPoints: questions * 5,
    targetMinutes: Math.max(15, Math.round(questions * 1.6)),
    enrolledCount: 28 + (questions % 8) * 16,
    liveStartRate: 0.52 + (questions % 5) * 0.08,
  }
}

/** Returns ≤4 KPI tiles for the assessment detail header band. */
export function assessmentDetailStageMetrics(
  ctx: AssessmentDetailKpiContext,
): MetricItem[] {
  switch (ctx.status) {
    case "Draft":
      return buildingMetrics(ctx)
    case "Scheduled":
      return scheduledMetrics(ctx)
    case "Live":
      return liveMetrics(ctx)
    case "Evaluating":
      return gradingMetrics(ctx)
    case "Results Published":
      return completedMetrics(ctx)
    default:
      return buildingMetrics(ctx)
  }
}

/** Derive submission-side figures for stage KPIs from mock rows. */
export function assessmentSubmissionKpiFigures(
  submissions: Array<{ status: string; score: number; submittedAt?: string }>,
  options?: {
    enrolledCount?: number
    status?: string
    liveStartRate?: number
  },
): Pick<
  AssessmentDetailKpiContext,
  | "enrolledCount"
  | "startedCount"
  | "submittedCount"
  | "gradedCount"
  | "pendingManualCount"
  | "avgScore"
  | "minScore"
  | "maxScore"
  | "scoreBands"
  | "passRate"
  | "autoGradedPercent"
> {
  const enrolledCount = options?.enrolledCount ?? 145
  const liveStartRate = options?.liveStartRate ?? 0.86
  const withSubmission = submissions.filter(
    (row) => row.submittedAt && row.status !== "",
  )
  const submittedCount = withSubmission.length
  const graded = withSubmission.filter((row) => row.status === "Graded")
  const gradedCount = graded.length
  const pendingManualCount = withSubmission.filter(
    (row) => row.status === "In-grading",
  ).length
  const scores = graded.map((row) => row.score).filter((score) => score > 0)
  const avgScore =
    scores.length > 0
      ? scores.reduce((sum, score) => sum + score, 0) / scores.length
      : null
  const minScore = scores.length > 0 ? Math.min(...scores) : null
  const maxScore = scores.length > 0 ? Math.max(...scores) : null
  const passRate =
    scores.length > 0
      ? Math.round(
          (scores.filter((score) => score >= 70).length / scores.length) * 100,
        )
      : null

  const startedCount =
    options?.status === "Live"
      ? Math.max(submittedCount, Math.round(enrolledCount * liveStartRate))
      : submittedCount

  const autoGradedPercent =
    options?.status === "Live" && submittedCount > 0
      ? Math.round(((submittedCount - pendingManualCount) / submittedCount) * 100)
      : null

  return {
    enrolledCount,
    startedCount,
    submittedCount,
    gradedCount,
    pendingManualCount,
    avgScore,
    minScore,
    maxScore,
    scoreBands: scoreBandSeries(scores),
    passRate,
    autoGradedPercent,
  }
}
